import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, Plus, Trash2, ChevronDown, Search } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageToggle from "@/components/LanguageToggle";
import VisitorPrizeNotice from "@/components/VisitorPrizeNotice";
import { API, EVENT } from "@/lib/constants";
import { VISITOR_COUNTRIES, searchCountries, bilingualCountry } from "@/lib/countries";

// Rev 7.10 (Feb 2026 owner UX overhaul):
//   * Required lead fields: full name, mobile, email, AGE, city, country.
//   * Optional: lead sex, family members' sex/age, interests, assistance,
//     marketing consent, day plan.
//   * ONE required ticket-ack checkbox above Submit (base wording while
//     prize flag off; augmented with the lead-only prize clause when the
//     flag is on). Server enforces the acknowledgment either way.
//   * Country selector is a bilingual searchable combobox drawn from
//     `VISITOR_COUNTRIES` (Palestine included; Israel intentionally not
//     present in the list).
const DAYS = ["dec10", "dec11", "dec12"];
// Rev 7.11 (Feb 2026 owner directive) — FOUR optional questions.
// F1 memorabilia removed; each multi-select question includes a
// mutually-exclusive "no_preference" token. Two new SINGLE-select
// budget questions. The old "multiday" question is removed from the
// public form.
const INTEREST_EXPERIENCE = ["vehicle_displays", "sim_racing", "community_meet", "no_preference"];
const INTEREST_VEHICLES = ["classic", "modified_jdm", "supercars", "motorcycles", "no_preference"];
const BUDGET_PURCHASE = ["le_150k", "150k_300k", "300k_500k", "gt_500k", "not_considering", "prefer_not_to_say"];
const BUDGET_MODIFICATION = ["le_25k", "25k_50k", "50k_100k", "gt_100k", "not_considering", "prefer_not_to_say"];
// Feb 2026 — owner removed the per-question maximum on the two
// earlier CHECKBOX-style interest questions. Visitor may pick any
// subset of choices, up to and including ALL choices. The final two
// budget questions remain SINGLE-select (radio-style) via
// <SingleChoice>. This constant is retained only as documentation.
const MAX_INTEREST_PICKS = null;
const MAX_GROUP = 10;
const MAX_ADDS = MAX_GROUP - 1;

export default function VisitorRegistration() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [searchParams] = useSearchParams();

  // ?preview_prize=1 gated behind admin JWT so it cannot leak in prod.
  const previewPrizeAuthorized = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (searchParams.get("preview_prize") !== "1") return false;
    try {
      return !!localStorage.getItem("admin_token");
    } catch (_) {
      return false;
    }
  }, [searchParams]);

  const [prizeCfg, setPrizeCfg] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    city: "",
    country: "",
    lead_age: "",
    lead_sex: "",
    interest_experience: [],
    interest_vehicles: [],
    budget_purchase: "",
    budget_modification: "",
    family_members: [],
    planned_days: [],
    privacy_accepted: false,
    marketing_consent: false,
    ticket_ack_accepted: false,
    assistance_answer: "",
    assistance_needs: [],
    assistance_note: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/visitor/prize-terms`)
      .then((r) => { if (!cancelled) setPrizeCfg(r.data); })
      .catch(() => { if (!cancelled) setPrizeCfg({ enabled: false }); });
    return () => { cancelled = true; };
  }, []);

  const prizeFlagOn = !!(prizeCfg && prizeCfg.enabled);
  const showPrizeCopy = prizeFlagOn || previewPrizeAuthorized;

  const groupCount = 1 + (form.family_members?.length || 0);
  const canAddFamily = groupCount < MAX_GROUP;

  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };
  const setFamilyField = (idx, k, v) => setForm((f) => ({
    ...f,
    family_members: (f.family_members || []).map((m, i) => i === idx ? { ...m, [k]: v } : m),
  }));
  const addFamily = () => setForm((f) => {
    if ((f.family_members || []).length >= MAX_ADDS) return f;
    return { ...f, family_members: [...(f.family_members || []), { sex: "", age: "" }] };
  });
  const removeFamily = (idx) => setForm((f) => ({
    ...f, family_members: (f.family_members || []).filter((_, i) => i !== idx),
  }));
  const toggleDay = (d) => setForm((f) => ({
    ...f,
    planned_days: f.planned_days.includes(d) ? f.planned_days.filter((x) => x !== d) : [...f.planned_days, d],
  }));
  const toggleInterest = (fieldKey, tok) => setForm((f) => {
    const cur = f[fieldKey] || [];
    // Rev 7.11 — mutually exclusive "no_preference":
    // * clicking it clears everything else
    // * clicking any specific option while "no_preference" is on
    //   clears "no_preference"
    if (tok === "no_preference") {
      return { ...f, [fieldKey]: cur.includes("no_preference") ? [] : ["no_preference"] };
    }
    let next = cur.filter((x) => x !== "no_preference");
    if (next.includes(tok)) next = next.filter((x) => x !== tok);
    else next = [...next, tok];   // Feb 2026 — no per-question cap; visitor may select any subset up to all.
    return { ...f, [fieldKey]: next };
  });
  const toggleAssistNeed = (key) => setForm((f) => ({
    ...f,
    assistance_needs: f.assistance_needs.includes(key) ? f.assistance_needs.filter((x) => x !== key) : [...f.assistance_needs, key],
  }));
  const setAssistanceAnswer = (val) => setForm((f) => ({
    ...f,
    assistance_answer: val,
    assistance_needs: val === "yes" ? f.assistance_needs : [],
    assistance_note: val === "yes" ? f.assistance_note : "",
  }));

  const validate = () => {
    const e = {};
    if (!form.full_name || form.full_name.trim().length < 2) e.full_name = t("visitorErrRequired");
    if (!form.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = t("visitorErrEmailRequired");
    const cleanedMobile = (form.mobile || "").replace(/[\s\-()]/g, "");
    if (!cleanedMobile.startsWith("+") || cleanedMobile.length < 7) e.mobile = t("visitorErrMobile");
    if (!form.city || form.city.trim().length < 1) e.city = t("visitorErrCity");
    if (!form.country) e.country = t("visitorErrCountry");
    const leadAgeN = Number(form.lead_age);
    if (!Number.isInteger(leadAgeN) || leadAgeN < 0 || leadAgeN > 120 || String(form.lead_age).trim() === "") e.lead_age = t("visitorErrLeadAge");
    // Feb 2026 owner directive: lead gender is REQUIRED (prospective —
    // legacy visitor rows with empty lead_sex remain valid and are
    // never rejected/backfilled).
    if (form.lead_sex !== "male" && form.lead_sex !== "female") e.lead_sex = t("visitorErrLeadSex");
    // Family: Feb 2026 owner directive — every added family member MUST
    // supply gender AND age. Per-row errors are stored in e.family_rows
    // keyed by index so each row can render its own message. The legacy
    // e.family aggregate is kept for the max-group cap only. Existing
    // stored rows are never revalidated.
    const fam = form.family_members || [];
    if (fam.length > MAX_ADDS) e.family = t("visitorErrMaxGroup");
    else {
      const rowErrs = {};
      for (let i = 0; i < fam.length; i++) {
        const m = fam[i];
        if (m.sex !== "male" && m.sex !== "female") {
          rowErrs[i] = { ...(rowErrs[i] || {}), sex: t("visitorErrFamilySex") };
        }
        const ageStr = String(m.age ?? "").trim();
        if (ageStr === "") {
          rowErrs[i] = { ...(rowErrs[i] || {}), age: t("visitorErrFamilyAge") };
        } else {
          const n = Number(m.age);
          if (!Number.isInteger(n) || n < 0 || n > 120) {
            rowErrs[i] = { ...(rowErrs[i] || {}), age: t("visitorErrFamilyAge") };
          }
        }
      }
      if (Object.keys(rowErrs).length) e.family_rows = rowErrs;
    }
    if (!form.privacy_accepted) e.privacy_accepted = t("visitorErrPrivacy");
    if (!form.ticket_ack_accepted) e.ticket_ack_accepted = t("visitorErrTicketAck");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error(t("visitorErrRequired")); return; }
    setSubmitting(true);
    try {
      const cleanedMobile = form.mobile.replace(/[\s\-()]/g, "");
      // persons = optional family details only (lead's own age/sex live
      // in lead_age/lead_sex). Rows with blank sex/age are dropped so
      // the backend doesn't store empty entries.
      const persons = (form.family_members || [])
        .map((m) => ({ sex: m.sex, age: Number(m.age) }));
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        mobile: cleanedMobile,
        city: form.city.trim(),
        country: form.country,
        lead_age: leadAgeN(form.lead_age),
        lead_sex: form.lead_sex || "",
        total_attending: 1 + (form.family_members || []).length,
        persons,
        interest_experience: form.interest_experience || [],
        interest_vehicles: form.interest_vehicles || [],
        budget_purchase: form.budget_purchase || "",
        budget_modification: form.budget_modification || "",
        planned_days: form.planned_days,
        marketing_consent: !!form.marketing_consent,
        privacy_accepted: !!form.privacy_accepted,
        lang: isRTL ? "ar" : "en",
        ticket_ack_accepted: !!form.ticket_ack_accepted,
        ticket_ack_version: (prizeFlagOn && prizeCfg?.ticket_ack_version_with_prize)
          ? prizeCfg.ticket_ack_version_with_prize
          : (prizeCfg?.ticket_ack_version_base || ""),
      };
      if (form.assistance_answer) {
        payload.assistance_answer = form.assistance_answer;
        if (form.assistance_answer === "yes") {
          payload.assistance_needs = form.assistance_needs;
          payload.assistance_note = (form.assistance_note || "").trim();
        }
      }
      const res = await axios.post(`${API}/visitor/register`, payload);
      const ref = res.data?.visitor_ref;
      if (!ref) throw new Error("no reference returned");
      navigate(`/visitor/thank-you?ref=${encodeURIComponent(ref)}${res.data?.duplicate ? "&dup=1" : ""}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || d).join(" · ")
        : (typeof detail === "string" ? detail : (err?.message || "Something went wrong"));
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const dirAttr = isRTL ? "rtl" : "ltr";
  const arFont = isRTL
    ? { fontFamily: "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif" }
    : undefined;
  const groupCountEN = `Your ticket covers ${groupCount} ${groupCount === 1 ? "person" : "people"} including you.`;
  const groupCountAR = `تذكرتك تشمل ${groupCount} ${groupCount === 1 ? "شخصًا" : "أشخاص"} بمن فيهم أنت.`;
  const requiredTag = t("visitorTagRequired");
  const optionalTag = t("visitorTagOptional");

  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1200px 600px at 50% -10%, rgba(245,197,24,0.08), transparent 60%)" }} />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-8 pt-6 pb-16" dir={dirAttr}>
        <div className={`flex items-center justify-between mb-4 ${isRTL ? "flex-row-reverse" : ""}`}>
          <button
            type="button"
            onClick={() => navigate("/")}
            className={
              isRTL
                ? "text-sm text-white/60 hover:text-[#F5C518] transition-colors"
                : "text-xs tracking-[0.2em] uppercase text-white/50 hover:text-[#F5C518] transition-colors"
            }
            style={arFont}
            data-testid="visitor-back-home"
          >
            {isRTL ? "→ الرئيسية" : "← Home"}
          </button>
          <LanguageToggle />
        </div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className={`font-heading font-extrabold text-white ${isRTL ? "text-4xl sm:text-5xl" : "text-3xl sm:text-5xl"}`} style={arFont} data-testid="visitor-form-title">
            {t("visitorFormTitle")}
          </h1>
          <p className="mt-3 text-white/80 text-base sm:text-lg" style={arFont} data-testid="visitor-form-subtitle">{t("visitorFormSubtitle")}</p>
          <div className="mt-5 bg-[#F5C518] px-5 py-4 sm:px-6 sm:py-5" data-testid="visitor-free-notice">
            <p className={`text-black font-bold leading-snug ${isRTL ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"}`} style={arFont}>{t("visitorFreeNotice")}</p>
          </div>
        </motion.div>

        <form onSubmit={submit} className="mt-8 space-y-7" data-testid="visitor-form" noValidate>
          <div className="border border-white/15 bg-white/[0.02] px-5 py-5 sm:px-6 sm:py-6" data-testid="visitor-lead-section">
            <div className={`mb-4 text-xl sm:text-2xl text-white font-extrabold ${isRTL ? "text-right" : "text-left"}`} style={arFont}>{t("visitorLeadSectionTitle")}</div>
            <div className="space-y-5">
              <Field label={t("visitorFullName")} tag={requiredTag} error={errors.full_name} isRTL={isRTL}>
                <input type="text" value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} placeholder={t("visitorFullNamePh")} maxLength={120} data-testid="visitor-full-name-input" className={inputCls(errors.full_name)} dir="auto" />
              </Field>
              <Field label={t("visitorMobile")} tag={requiredTag} error={errors.mobile} isRTL={isRTL}>
                <input type="tel" value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} placeholder={t("visitorMobilePh")} maxLength={22} data-testid="visitor-mobile-input" className={inputCls(errors.mobile)} dir="ltr" autoComplete="tel" inputMode="tel" />
                <p className="mt-2 text-white/85 text-base sm:text-lg leading-relaxed" style={arFont}>{t("visitorMobileHelper")}</p>
              </Field>
              <Field label={t("visitorEmail")} tag={requiredTag} error={errors.email} isRTL={isRTL}>
                <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder={t("visitorEmailPh")} maxLength={200} data-testid="visitor-email-input" className={inputCls(errors.email)} dir="ltr" autoComplete="email" />
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label={t("visitorLeadAge")} tag={requiredTag} error={errors.lead_age} isRTL={isRTL}>
                  <input type="number" min={0} max={120} step={1} value={form.lead_age} onChange={(e) => setField("lead_age", e.target.value)} placeholder="0-120" data-testid="visitor-lead-age-input" className={inputCls(errors.lead_age)} dir="ltr" inputMode="numeric" />
                </Field>
                <Field label={t("visitorLeadSex")} tag={requiredTag} error={errors.lead_sex} isRTL={isRTL}>
                  <div className="grid grid-cols-2 gap-3">
                    {["male", "female"].map((v) => {
                      const on = form.lead_sex === v;
                      return (
                        <button type="button" key={v} onClick={() => setField("lead_sex", on ? "" : v)} data-testid={`visitor-lead-sex-${v}`}
                          className={`border px-4 py-4 min-h-[52px] text-lg sm:text-xl font-semibold transition-colors ${on ? "border-[#F5C518] bg-[#F5C518]/10 text-[#F5C518]" : "border-white/25 bg-white/[0.02] text-white hover:border-white/60"}`} style={arFont}>
                          {t(`visitorSex_${v}`)}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </div>
              <Field label={t("visitorCity")} tag={requiredTag} error={errors.city} isRTL={isRTL}>
                <input type="text" value={form.city} onChange={(e) => setField("city", e.target.value)} placeholder={t("visitorCityPh")} maxLength={100} data-testid="visitor-city-input" className={inputCls(errors.city)} dir="auto" autoComplete="address-level2" />
              </Field>
              <Field label={t("visitorCountry")} tag={requiredTag} error={errors.country} isRTL={isRTL}>
                <CountryCombobox
                  value={form.country}
                  onChange={(v) => setField("country", v)}
                  isRTL={isRTL}
                  labelSelect={t("visitorCountryPh")}
                  labelSearch={t("visitorCountrySearchPh")}
                  hasError={!!errors.country}
                />
              </Field>
            </div>
          </div>

          <div className="border border-white/15 bg-white/[0.02] px-5 py-5 sm:px-6 sm:py-6" data-testid="visitor-family-section">
            <div className={`flex items-start ${isRTL ? "flex-row-reverse text-right" : ""} justify-between gap-3 mb-2`}>
              <div className={isRTL ? "text-right" : "text-left"}>
                <div className="text-xl sm:text-2xl text-white font-extrabold" style={arFont}>
                  {t("visitorFamilySectionTitle")}{" "}
                  <span className="inline-block ms-2 text-xs uppercase tracking-widest px-2 py-1 border border-white/25 text-white/70 align-middle" style={arFont}>{optionalTag}</span>
                </div>
                <p className="mt-1 text-white/80 text-base sm:text-lg leading-relaxed" style={arFont}>{t("visitorFamilySectionHelper")}</p>
              </div>
            </div>
            {(form.family_members || []).length > 0 && (
              <div className="mt-4 space-y-3">
                {form.family_members.map((m, i) => {
                  const n = i + 2;
                  const nLabel = isRTL ? String(n).replace(/[0-9]/g, (d) => "٠١٢٣٤٥٦٧٨٩"[Number(d)]) : String(n);
                  const rowErr = (errors.family_rows && errors.family_rows[i]) || {};
                  return (
                    <div key={`fam-${i}`} className="border border-white/20 bg-white/[0.03] p-4" data-testid={`visitor-family-row-${i}`}>
                      <div className={`flex items-center justify-between mb-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                        <div className="text-lg sm:text-xl text-white font-bold" style={arFont}>{t("visitorFamilyRowLabel").replace("{n}", nLabel)}</div>
                        <button type="button" onClick={() => removeFamily(i)} data-testid={`visitor-family-remove-${i}`}
                          className="inline-flex items-center gap-2 px-3 py-2 min-h-[44px] border border-white/25 text-white/80 hover:border-red-400 hover:text-red-400 text-base font-semibold transition-colors" style={arFont} aria-label={t("visitorFamilyRemove")}>
                          <Trash2 className="w-5 h-5" /><span>{t("visitorFamilyRemove")}</span>
                        </button>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className={`block mb-1 text-base sm:text-lg text-white/90 font-semibold ${isRTL ? "text-right" : "text-left"}`} style={arFont}>
                            {t("visitorPersonSex")}{" "}<span className="text-[#F5C518] text-sm font-bold">· {requiredTag}</span>
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            {["male", "female"].map((v) => {
                              const on = m.sex === v;
                              return (
                                <button type="button" key={v} onClick={() => setFamilyField(i, "sex", on ? "" : v)} data-testid={`visitor-family-${i}-sex-${v}`}
                                  className={`border px-3 py-3 min-h-[48px] text-base sm:text-lg font-semibold transition-colors ${on ? "border-[#F5C518] bg-[#F5C518]/10 text-[#F5C518]" : "border-white/25 bg-white/[0.02] text-white hover:border-white/60"}`} style={arFont}>
                                  {t(`visitorSex_${v}`)}
                                </button>
                              );
                            })}
                          </div>
                          {rowErr.sex && (
                            <p className="mt-2 text-red-400 text-sm font-semibold" role="alert" style={arFont} data-testid={`visitor-family-${i}-sex-error`}>{rowErr.sex}</p>
                          )}
                        </div>
                        <div>
                          <label className={`block mb-1 text-base sm:text-lg text-white/90 font-semibold ${isRTL ? "text-right" : "text-left"}`} style={arFont}>
                            {t("visitorPersonAge")}{" "}<span className="text-[#F5C518] text-sm font-bold">· {requiredTag}</span>
                          </label>
                          <input type="number" min={0} max={120} step={1} value={m.age} onChange={(e) => setFamilyField(i, "age", e.target.value)} data-testid={`visitor-family-${i}-age`} className={inputCls(!!rowErr.age)} dir="ltr" inputMode="numeric" placeholder="0-120" />
                          {rowErr.age && (
                            <p className="mt-2 text-red-400 text-sm font-semibold" role="alert" style={arFont} data-testid={`visitor-family-${i}-age-error`}>{rowErr.age}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-4">
              <button type="button" onClick={addFamily} disabled={!canAddFamily} data-testid="visitor-family-add"
                className={`inline-flex items-center gap-2 px-5 py-3 min-h-[48px] border text-lg font-semibold transition-colors ${canAddFamily ? "border-[#F5C518] text-[#F5C518] hover:bg-[#F5C518] hover:text-black" : "border-white/25 text-white/40 cursor-not-allowed"}`} style={arFont}>
                <Plus className="w-5 h-5" /><span>{t("visitorFamilyAdd")}</span>
              </button>
              {!canAddFamily && (
                <p className={`mt-2 text-white/60 text-base sm:text-lg ${isRTL ? "text-right" : "text-left"}`} style={arFont} data-testid="visitor-family-max-hint">{t("visitorFamilyMaxHint")}</p>
              )}
            </div>
            {errors.family && (<p className="mt-3 text-red-400 text-base font-semibold" role="alert" style={arFont} data-testid="visitor-family-error">{errors.family}</p>)}
            <div className="mt-5 border-t border-white/10 pt-4" data-testid="visitor-group-count">
              <p dir="ltr" className="text-white font-bold text-lg sm:text-xl leading-snug" data-testid="visitor-group-count-en">{groupCountEN}</p>
              <p lang="ar" dir="rtl" className="mt-1 text-white font-bold text-lg sm:text-xl leading-snug text-right"
                style={{ fontFamily: "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif" }} data-testid="visitor-group-count-ar">{groupCountAR}</p>
            </div>
          </div>

          <div>
            <div className={`mb-3 text-xl sm:text-2xl text-white font-extrabold ${isRTL ? "text-right" : "text-left"}`} style={arFont}>
              {t("visitorPlannedDays")}{" "}<span className="inline-block ms-2 text-xs uppercase tracking-widest px-2 py-1 border border-white/25 text-white/70 align-middle" style={arFont}>{optionalTag}</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {DAYS.map((d) => {
                const selected = form.planned_days.includes(d);
                return (
                  <button type="button" key={d} onClick={() => toggleDay(d)} data-testid={`visitor-day-${d}`}
                    className={`text-left border px-4 py-4 min-h-[60px] transition-colors ${selected ? "border-[#F5C518] bg-[#F5C518]/10 text-[#F5C518]" : "border-white/25 bg-white/[0.02] text-white hover:border-white/60"}`} style={{ textAlign: isRTL ? "right" : "left" }}>
                    <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                      <span className={`w-6 h-6 border ${selected ? "border-[#F5C518] bg-[#F5C518]" : "border-white/50"} flex items-center justify-center`}>{selected && <Check className="w-4 h-4 text-black" />}</span>
                      <span className="text-lg sm:text-xl font-semibold" style={arFont}>{t(`visitorDay_${d}`)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-white/85 text-base sm:text-lg leading-relaxed" style={arFont}>{t("visitorPlannedDaysHint")}</p>
          </div>

          {/* Rev 7.11 — FOUR distinct optional questions */}
          <InterestQuestion
            testid="visitor-interest-experience"
            title={t("visitorInterestExperienceTitle")}
            optionalTag={t("visitorAnsweringOptional")}
            maxHint={t("visitorInterestPickUpTo2").replace("{n}", String(form.interest_experience.length))}
            choices={INTEREST_EXPERIENCE}
            selected={form.interest_experience}
            onToggle={(tok) => toggleInterest("interest_experience", tok)}
            labelFn={(k) => t(`visitorIExp_${k}`)}
            isRTL={isRTL}
            arFont={arFont}
          />
          <InterestQuestion
            testid="visitor-interest-vehicles"
            title={t("visitorInterestVehiclesTitle")}
            optionalTag={t("visitorAnsweringOptional")}
            maxHint={t("visitorInterestPickUpTo2").replace("{n}", String(form.interest_vehicles.length))}
            choices={INTEREST_VEHICLES}
            selected={form.interest_vehicles}
            onToggle={(tok) => toggleInterest("interest_vehicles", tok)}
            labelFn={(k) => t(`visitorIVeh_${k}`)}
            isRTL={isRTL}
            arFont={arFont}
          />
          <SingleChoice
            testid="visitor-budget-purchase"
            title={t("visitorBudgetPurchaseTitle")}
            optionalTag={t("visitorAnsweringOptional")}
            hint={t("visitorPickOne")}
            choices={BUDGET_PURCHASE}
            value={form.budget_purchase}
            onChange={(v) => setField("budget_purchase", v)}
            labelFn={(k) => t(`visitorBudP_${k}`)}
            isRTL={isRTL}
            arFont={arFont}
          />
          <SingleChoice
            testid="visitor-budget-modification"
            title={t("visitorBudgetModTitle")}
            optionalTag={t("visitorAnsweringOptional")}
            hint={t("visitorPickOne")}
            choices={BUDGET_MODIFICATION}
            value={form.budget_modification}
            onChange={(v) => setField("budget_modification", v)}
            labelFn={(k) => t(`visitorBudM_${k}`)}
            isRTL={isRTL}
            arFont={arFont}
          />

          {/* Assistance */}
          <div className="border border-white/15 bg-white/[0.03] px-5 py-5 sm:px-6 sm:py-6" data-testid="visitor-assistance-section">
            <div className={`text-xl sm:text-2xl text-white font-extrabold leading-snug mb-2 ${isRTL ? "text-right" : "text-left"}`} style={arFont}>
              {t("visitorAssistanceTitle")}{" "}<span className="inline-block ms-2 text-xs uppercase tracking-widest px-2 py-1 border border-white/25 text-white/70 align-middle" style={arFont}>{optionalTag}</span>
            </div>
            <p className={`text-white/85 text-base sm:text-lg leading-relaxed mb-4 ${isRTL ? "text-right" : "text-left"}`} style={arFont}>{t("visitorAssistanceHelper")}</p>
            <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label={t("visitorAssistanceTitle")}>
              {["no", "yes", "prefer_not_to_say"].map((val) => {
                const selected = form.assistance_answer === val;
                return (
                  <button type="button" key={val} role="radio" aria-checked={selected} onClick={() => setAssistanceAnswer(val)} data-testid={`visitor-assist-answer-${val}`}
                    className={`text-left border px-4 py-4 min-h-[60px] transition-colors ${selected ? "border-[#F5C518] bg-[#F5C518]/10 text-[#F5C518]" : "border-white/25 bg-white/[0.02] text-white hover:border-white/60"}`} style={{ textAlign: isRTL ? "right" : "left" }}>
                    <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                      <span className={`w-6 h-6 rounded-full border-2 ${selected ? "border-[#F5C518] bg-[#F5C518]" : "border-white/50"} flex items-center justify-center`}>{selected && <Check className="w-4 h-4 text-black" />}</span>
                      <span className="text-lg sm:text-xl font-semibold" style={arFont}>{t(`visitorAssistanceAnswer_${val}`)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            {form.assistance_answer === "yes" && (
              <div className="mt-5 border-t border-white/10 pt-5" data-testid="visitor-assist-followup">
                <div className={`text-lg sm:text-xl text-white font-bold mb-3 ${isRTL ? "text-right" : "text-left"}`} style={arFont}>{t("visitorAssistanceNeedsTitle")}</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {["mobility", "hearing", "visual", "other"].map((k) => {
                    const on = form.assistance_needs.includes(k);
                    return (
                      <label key={k} className={`flex items-start gap-3 border px-4 py-3 min-h-[52px] cursor-pointer transition-colors ${on ? "border-[#F5C518] bg-[#F5C518]/[0.06]" : "border-white/25 bg-white/[0.02] hover:border-white/60"} ${isRTL ? "flex-row-reverse text-right" : ""}`} data-testid={`visitor-assist-need-${k}-wrap`}>
                        <input type="checkbox" checked={on} onChange={() => toggleAssistNeed(k)} className="mt-1 w-6 h-6 accent-[#F5C518]" data-testid={`visitor-assist-need-${k}`} />
                        <span className="text-white text-base sm:text-lg leading-snug font-semibold" style={arFont}>{t(`visitorAssistanceNeed_${k}`)}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-4">
                  <label className={`block mb-2 text-base sm:text-lg text-white/90 font-semibold ${isRTL ? "text-right" : "text-left"}`} style={arFont}>{t("visitorAssistanceNote")}</label>
                  <textarea value={form.assistance_note} onChange={(e) => setField("assistance_note", e.target.value)} placeholder={t("visitorAssistanceNotePh")} maxLength={500} rows={3} data-testid="visitor-assist-note"
                    className="w-full bg-black/40 border border-white/25 px-4 py-4 min-h-[96px] text-white text-lg leading-relaxed placeholder:text-white/40 focus:outline-none focus:border-[#F5C518] transition-colors" dir={isRTL ? "rtl" : "ltr"} />
                  <p className={`mt-2 text-white/70 text-sm sm:text-base ${isRTL ? "text-right" : "text-left"}`} style={arFont}>{(form.assistance_note || "").length}/500</p>
                </div>
                <p className={`mt-4 text-amber-200/90 text-base sm:text-lg leading-relaxed font-semibold ${isRTL ? "text-right" : "text-left"}`} style={arFont} data-testid="visitor-assist-guarantee-notice">{t("visitorAssistanceGuaranteeNotice")}</p>
              </div>
            )}
          </div>

          {/* Feb 2026 owner-approved Visitor Prize Draw NOTICE
              (informational only; sits above privacy/acknowledgement). */}
          <VisitorPrizeNotice />

          {/* Privacy */}
          <div className="border border-white/15 bg-white/[0.03] px-5 py-5 sm:px-6 sm:py-6">
            <div className="text-xl sm:text-2xl text-white font-extrabold mb-3" style={arFont}>{t("visitorPrivacyLabel")}</div>
            <p className="text-white/90 leading-relaxed text-base sm:text-lg" style={arFont}>{t("visitorPrivacyText")}</p>
            <label className={`mt-4 flex items-start gap-3 cursor-pointer min-h-[52px] ${isRTL ? "flex-row-reverse text-right" : ""}`}>
              <input type="checkbox" checked={form.privacy_accepted} onChange={(e) => setField("privacy_accepted", e.target.checked)} data-testid="visitor-privacy-accept" className="mt-1.5 w-6 h-6 accent-[#F5C518]" />
              <span className="text-white text-lg sm:text-xl leading-snug font-semibold" style={arFont}>{t("visitorPrivacyAccept")}</span>
            </label>
            {errors.privacy_accepted && (<p className="mt-2 text-red-400 text-base font-semibold" data-testid="visitor-privacy-error">{errors.privacy_accepted}</p>)}
          </div>

          <label className={`flex items-start gap-3 cursor-pointer min-h-[52px] ${isRTL ? "flex-row-reverse text-right" : ""}`}>
            <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setField("marketing_consent", e.target.checked)} data-testid="visitor-marketing-consent" className="mt-1.5 w-6 h-6 accent-[#F5C518]" />
            <span className="text-white/90 text-base sm:text-lg leading-snug" style={arFont}>{t("visitorMarketingConsent")}</span>
          </label>

          {/* Rev 7.10 — ONE required ticket-ack. Base wording always; when
              prize flag is on (or admin preview), the prize clause is
              rendered inside the same bordered block above the same
              checkbox. */}
          <div className="border-2 border-[#F5C518] bg-black px-5 py-5 sm:px-6 sm:py-6" data-testid="visitor-ticket-ack-block">
            {previewPrizeAuthorized && !prizeFlagOn && (
              <div className="mb-3 inline-block bg-amber-500 text-black text-[11px] font-extrabold uppercase tracking-widest px-3 py-1" data-testid="visitor-prize-preview-badge">
                Draft · Not Public · admin preview
              </div>
            )}
            <p dir="ltr" className="text-white font-extrabold leading-relaxed text-lg sm:text-2xl" data-testid="visitor-ticket-ack-en">
              {t("visitorTicketAckEN")}{showPrizeCopy ? ` ${prizeCfg?.en || ""}` : ""}
            </p>
            <div className="h-3" />
            <p lang="ar" dir="rtl" className="text-white font-extrabold leading-relaxed text-xl sm:text-2xl text-right"
              style={{ fontFamily: "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif" }} data-testid="visitor-ticket-ack-ar">
              {t("visitorTicketAckAR")}{showPrizeCopy ? ` ${prizeCfg?.ar || ""}` : ""}
            </p>

            <label className={`mt-5 flex items-start gap-3 cursor-pointer min-h-[52px] ${isRTL ? "flex-row-reverse text-right" : ""}`}>
              <input type="checkbox" checked={!!form.ticket_ack_accepted} onChange={(e) => setField("ticket_ack_accepted", e.target.checked)}
                data-testid="visitor-ticket-ack-checkbox" required aria-required className="mt-1.5 w-6 h-6 accent-[#F5C518]" />
              <span className="flex flex-col gap-2">
                <span dir="ltr" className="text-white text-lg sm:text-xl font-bold leading-snug" data-testid="visitor-ticket-ack-check-en">
                  {t("visitorTicketAckCheckEN")}
                </span>
                <span lang="ar" dir="rtl" className="text-white text-xl sm:text-2xl font-bold leading-snug text-right"
                  style={{ fontFamily: "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif" }} data-testid="visitor-ticket-ack-check-ar">
                  {t("visitorTicketAckCheckAR")}
                </span>
              </span>
            </label>
            {errors.ticket_ack_accepted && (<p className="mt-3 text-red-400 text-base font-semibold" role="alert" data-testid="visitor-ticket-ack-error">{errors.ticket_ack_accepted}</p>)}
          </div>

          <div className={`pt-2 flex ${isRTL ? "justify-start" : "justify-end"}`}>
            <button type="submit" disabled={submitting} data-testid="visitor-submit-btn"
              className={`btn-primary group inline-flex items-center gap-3 ${submitting ? "opacity-60 cursor-not-allowed" : ""}`}>
              {submitting ? t("visitorSubmitting") : t("visitorSubmit")}
              {!submitting && (isRTL ? <ArrowRight className="w-4 h-4 rotate-180 transition-transform group-hover:-translate-x-1" /> : <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function leadAgeN(v) {
  const n = Number(v); return Number.isInteger(n) ? n : 0;
}

function Field({ label, tag, error, isRTL, children }) {
  const arFont = isRTL ? { fontFamily: "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif" } : undefined;
  return (
    <div>
      <label className={`block mb-2 text-lg sm:text-xl text-white font-bold leading-snug ${isRTL ? "text-right" : "text-left"}`} style={arFont}>
        {label}
        {tag && (
          <span className={`inline-block ms-2 text-xs uppercase tracking-widest px-2 py-1 border ${tag && (tag.includes("Required") || tag.includes("مطلوب")) ? "border-[#F5C518] text-[#F5C518]" : "border-white/25 text-white/70"} align-middle`} style={arFont}>{tag}</span>
        )}
      </label>
      {children}
      {error && (<p className="mt-2 text-red-400 text-base font-semibold" role="alert" style={arFont}>{error}</p>)}
    </div>
  );
}

function inputCls(err) {
  const base = "w-full bg-black/40 border px-4 py-4 min-h-[52px] text-white text-lg sm:text-xl leading-snug placeholder:text-white/40 focus:outline-none focus:border-[#F5C518] transition-colors";
  return `${base} ${err ? "border-red-500/60" : "border-white/25"}`;
}

// Feb 2026 — owner removed the per-question maximum on these earlier
// checkbox interest questions. Renders a bilingual title tagged
// Optional, any subset of the choices (including all of them) can be
// selected, and enforces the mutually-exclusive "no_preference"
// behaviour visually. When "no_preference" is on, the other tiles
// remain visually disabled so the visitor sees the exclusivity.
function InterestQuestion({ testid, title, optionalTag, maxHint, choices, selected, onToggle, labelFn, isRTL, arFont }) {
  const noPrefOn = selected.includes("no_preference");
  return (
    <div className="border border-white/15 bg-white/[0.03] px-5 py-5 sm:px-6 sm:py-6" data-testid={`${testid}-section`}>
      <div className={`text-xl sm:text-2xl text-white font-extrabold leading-snug mb-2 ${isRTL ? "text-right" : "text-left"}`} style={arFont}>
        {title}
      </div>
      <p className={`text-white/85 text-base sm:text-lg leading-relaxed mb-1 font-semibold ${isRTL ? "text-right" : "text-left"}`} style={arFont} data-testid={`${testid}-optional`}>
        {optionalTag}
      </p>
      <p className={`text-white/70 text-base sm:text-lg leading-relaxed mb-4 ${isRTL ? "text-right" : "text-left"}`} style={arFont} data-testid={`${testid}-hint`}>
        {maxHint}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {choices.map((k) => {
          const on = selected.includes(k);
          const isNoPref = k === "no_preference";
          // When "no_preference" is on, other tiles are visibly
          // disabled (they will not toggle in). There is otherwise no
          // maximum-selection cap — the visitor can pick every
          // specific choice.
          const disabled = !on && noPrefOn && !isNoPref;
          return (
            <label
              key={k}
              data-testid={`${testid}-${k}-wrap`}
              className={`flex items-start gap-3 border px-4 py-3 min-h-[56px] transition-colors ${on ? "border-[#F5C518] bg-[#F5C518]/[0.06]" : disabled ? "border-white/10 bg-white/[0.01] opacity-50 cursor-not-allowed" : "border-white/25 bg-white/[0.02] hover:border-white/60 cursor-pointer"} ${isRTL ? "flex-row-reverse text-right" : ""}`}
            >
              <input
                type="checkbox"
                checked={on}
                disabled={disabled}
                onChange={() => onToggle(k)}
                data-testid={`${testid}-${k}`}
                className="mt-1 w-6 h-6 accent-[#F5C518]"
              />
              <span className="text-white text-base sm:text-lg leading-snug font-semibold" style={arFont}>
                {labelFn(k)}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// Rev 7.11 — single-select bilingual question. Renders a radio-style
// tile list with 48px+ tap targets. Clicking the selected tile again
// clears the answer (visitor may skip cleanly). Empty string is the
// canonical "skipped" value.
function SingleChoice({ testid, title, optionalTag, hint, choices, value, onChange, labelFn, isRTL, arFont }) {
  return (
    <div className="border border-white/15 bg-white/[0.03] px-5 py-5 sm:px-6 sm:py-6" data-testid={`${testid}-section`}>
      <div className={`text-xl sm:text-2xl text-white font-extrabold leading-snug mb-2 ${isRTL ? "text-right" : "text-left"}`} style={arFont}>
        {title}
      </div>
      <p className={`text-white/85 text-base sm:text-lg leading-relaxed mb-1 font-semibold ${isRTL ? "text-right" : "text-left"}`} style={arFont} data-testid={`${testid}-optional`}>
        {optionalTag}
      </p>
      <p className={`text-white/70 text-base sm:text-lg leading-relaxed mb-4 ${isRTL ? "text-right" : "text-left"}`} style={arFont}>
        {hint}
      </p>
      <div className="grid gap-3" role="radiogroup" aria-label={title}>
        {choices.map((k) => {
          const on = value === k;
          return (
            <button
              type="button"
              key={k}
              role="radio"
              aria-checked={on}
              onClick={() => onChange(on ? "" : k)}
              data-testid={`${testid}-${k}`}
              className={`text-left border px-4 py-3 min-h-[56px] transition-colors ${on ? "border-[#F5C518] bg-[#F5C518]/10 text-[#F5C518]" : "border-white/25 bg-white/[0.02] text-white hover:border-white/60"}`}
              style={{ textAlign: isRTL ? "right" : "left" }}
            >
              <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                <span className={`w-6 h-6 rounded-full border-2 ${on ? "border-[#F5C518] bg-[#F5C518]" : "border-white/50"} flex items-center justify-center`}>
                  {on && <Check className="w-4 h-4 text-black" />}
                </span>
                <span className="text-base sm:text-lg font-semibold leading-snug" style={arFont}>{labelFn(k)}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Bilingual searchable country combobox. Renders both EN + AR names
// side by side in every row so the selection is unambiguous
// regardless of the current language toggle.
function CountryCombobox({ value, onChange, isRTL, labelSelect, labelSearch, hasError }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);
  useEffect(() => {
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const list = useMemo(() => searchCountries(query), [query]);
  const selected = value ? bilingualCountry(value) : "";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="visitor-country-trigger"
        className={`w-full text-left bg-black/40 border px-4 py-4 min-h-[52px] text-white text-lg sm:text-xl leading-snug flex items-center justify-between gap-3 focus:outline-none focus:border-[#F5C518] transition-colors ${hasError ? "border-red-500/60" : "border-white/25"}`}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={selected ? "text-white" : "text-white/40"} dir={isRTL ? "rtl" : "ltr"}>
          {selected || labelSelect}
        </span>
        <ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-2 w-full bg-black border border-[#F5C518]/60 shadow-lg" data-testid="visitor-country-dropdown">
          <div className={`flex items-center gap-2 px-3 py-2 border-b border-white/10 ${isRTL ? "flex-row-reverse" : ""}`}>
            <Search className="w-4 h-4 text-white/50" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={labelSearch}
              data-testid="visitor-country-search"
              className="flex-1 bg-transparent text-white text-base sm:text-lg placeholder:text-white/40 focus:outline-none min-h-[44px]"
              dir="auto"
              autoComplete="off"
            />
          </div>
          <div className="max-h-72 overflow-y-auto" role="listbox">
            {list.length === 0 && (
              <div className="px-4 py-3 text-white/60 text-base" data-testid="visitor-country-none">{isRTL ? "لا نتائج" : "No matches"}</div>
            )}
            {list.map((c) => {
              const on = value === c.en;
              return (
                <button
                  key={c.en}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => { onChange(c.en); setOpen(false); setQuery(""); }}
                  data-testid={`visitor-country-option-${c.en.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 min-h-[48px] text-left transition-colors border-b border-white/5 last:border-b-0 ${on ? "bg-[#F5C518]/15 text-[#F5C518]" : "text-white hover:bg-white/[0.06]"}`}
                >
                  <span dir="ltr" className="text-base sm:text-lg font-medium">{c.en}</span>
                  <span lang="ar" dir="rtl" className="text-base sm:text-lg font-medium"
                    style={{ fontFamily: "'Noto Sans Arabic', 'Noto Kufi Arabic', 'Cairo', 'Tahoma', 'Segoe UI', Arial, sans-serif" }}>
                    {c.ar}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
